import { useCallback, useEffect, useMemo, useState } from "react";
import { ProductData, SortFSType } from "../types";

// Helper function to parse Italian formatted numbers (e.g., "1.234,56" -> 1234.56)
function parseItalianNumber(value: string): number {
  if (value === "-" || value === "" || value === null || value === undefined) {
    return -Infinity; // Sort "-" values to the end
  }
  // Remove thousands separator (.) and replace decimal separator (,) with (.)
  const normalized = String(value).replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? -Infinity : parsed;
}

interface UsePortfolioSortProps {
  data: ProductData[];
  sortableColumns: string[];
}

function usePortfolioSort({ data, sortableColumns }: UsePortfolioSortProps) {
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string;
  }>({
    key: null,
    direction: "asc",
  });

  const sortFSArray: SortFSType[] = [
    "ascFS",
    "descFS",
    "ascFSAdvice",
    "descFSAdvice",
  ];
  const [sortConfigFS, setSortConfigFS] = useState<number>(-1);

  // Sorting function with proper Italian number handling
  const sortedData = useMemo(() => {
    // If no sort config is set, return the original data
    if (!sortConfig.key) {
      return data;
    }

    let dynamicKey = sortConfig.key;
    let dynamicDirection = sortConfig.direction;

    // Handle special 4-state cycling for final_score_advice
    if (sortConfig.key === "final_score_advice") {
      switch (sortFSArray[sortConfigFS]) {
        case "ascFS":
          dynamicKey = "final_score";
          dynamicDirection = "asc";
          break;
        case "descFS":
          dynamicKey = "final_score";
          dynamicDirection = "desc";
          break;
        case "ascFSAdvice":
          dynamicKey = "final_score_advice";
          dynamicDirection = "asc";
          break;
        case "descFSAdvice":
          dynamicKey = "final_score_advice";
          dynamicDirection = "desc";
          break;
      }
    }

    return [...data].sort((a, b) => {
      const key = dynamicKey;

      // Apply different sorting logic based on the key
      switch (key) {
        case "peso_sul_ptf":
        case "controvalore":
        case "costi_totali_stimati":
        case "rating_ESG":
        case "final_score":
        case "final_score_advice":
        case "ytm":
        case "duration":
        case "pl_fiscale_euro":
        case "pl_fiscale_perc":
        case "rateo_euro":
        case "commissioni_gestione":
        case "abbuoni":
        case "dividend_yield":
          // Numeric sorting with Italian number parsing
          const numA = parseItalianNumber(a[key]);
          const numB = parseItalianNumber(b[key]);
          return dynamicDirection === "asc" ? numA - numB : numB - numA;

        case "scadenza":
          // Date sorting using raw yyyy-mm-dd value
          const dateA = a.scadenza_raw || "";
          const dateB = b.scadenza_raw || "";
          return dynamicDirection === "asc"
            ? dateA.localeCompare(dateB)
            : dateB.localeCompare(dateA);

        case "microcategoria_advice":
        case "emittente":
        case "tipo_emittente":
        case "tipo_bond":
        case "paese":
        case "frequenza_distribuzione_dividendo":
          // Alphabetical sorting
          const strA = String(a[key] || "").toLowerCase();
          const strB = String(b[key] || "").toLowerCase();
          return dynamicDirection === "asc"
            ? strA.localeCompare(strB)
            : strB.localeCompare(strA);

        default:
          // Default string sorting
          const valA = String(a[key] || "").toLowerCase();
          const valB = String(b[key] || "").toLowerCase();
          return dynamicDirection === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
      }
    });
  }, [data, sortConfig, sortConfigFS, sortFSArray]);

  const handleSort = useCallback(
    (key: string) => {
      // Only allow sorting on sortable columns
      if (!sortableColumns.includes(key)) {
        return;
      }

      if (key === "final_score_advice") {
        // Cycle through 4 states for final_score_advice
        let sortConfigFSUpdate = sortConfigFS + 1;
        if (sortConfigFSUpdate > sortFSArray.length - 1) {
          sortConfigFSUpdate = 0;
        }
        setSortConfigFS(sortConfigFSUpdate);
        setSortConfig((prevConfig) => {
          const direction = prevConfig.direction;
          return { key, direction };
        });
      } else {
        // Toggle between asc and desc for other columns
        setSortConfig((prevConfig) => {
          let direction = "asc";
          if (prevConfig.key === key && prevConfig.direction === "asc") {
            direction = "desc";
          }
          return { key, direction };
        });
      }
    },
    [sortConfigFS, sortableColumns, sortFSArray],
  );

  // Set default sort on mount
  useEffect(() => {
    handleSort("microcategoria_advice");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sortConfig,
    sortedData,
    handleSort,
  };
}

export default usePortfolioSort;
